// boreToWell / wellToBoreView — the Bore→Well cutover adapter (EPIC_BORE_TO_WELL_CUTOVER.md Phase 1).
//
// The NON-DESTRUCTIVE, LOSSLESS shim that lets the cutover land incrementally. `boreToWell` wraps a
// legacy Borefield/Bore into the canonical FieldPad/Well/Wellbore model, and `wellToBoreView`
// projects it back, so later phases migrate consumers one at a time.
//
// MINIMAL PROMOTION (the key correctness invariant): Phase 1 promotes to canonical homes ONLY the
// fields the new model actually reads today — the structural ids, `Well.wellName`, and
// `Wellbore.jobs` (used by the timeline + SIMOPS). EVERYTHING else is carried VERBATIM in
// `legacyBore` / `legacyBorefield` and read back from there. This keeps promotion and read-back
// SYMMETRIC — every promoted field is read back from its canonical home, so a new-model edit to it
// surfaces in the bore view, and nothing else can silently go stale. Each later phase that promotes
// a new field MUST add the matching read-back override here (and a round-trip test). The `legacy*`
// carries are deleted in Phase 7.
//
// TRULY NON-DESTRUCTIVE: the carries are DEEP CLONES (structuredClone) — wrapping a Bore can never
// mutate the original Bore/Borefield through the wrapper (Charter Absolute #7).
//
// SHIM IDENTITY: a Wellbore's id == the original Bore's id, so every legacy `boreId` reference keeps
// resolving through the transition (EPIC §4, spine doc §6.1).
//
// PURE: deterministic (the Well id derives from the Bore id; no Date.now() / generateId / randomness)
// so the round-trip is unit-testable. No engineering/economic/statistical math (Charter Abs-2).

import type { Bore, Borefield } from "../borefields/types";
import type { Well, Wellbore } from "./types";

/** A coordinate string is promotable only if it parses to a finite number (matches the pad-anchor
 *  validity guard in migrateBorefields — a non-numeric "TBD"/"" is dropped, not stored as garbage). */
function validCoordStr(v: string | undefined): boolean {
  return !!v && !Number.isNaN(parseFloat(v));
}

/** Deterministic Well id wrapping a legacy Bore (the flat Bore had no "well" tier).
 *  ASSUMES bore ids never start with "well-" (they don't — they're generateId/uuid shaped); the
 *  bore id is recovered from `wellbore.id` (== bore.id), NOT by stripping this prefix. */
export function wellIdForBore(boreId: string): string {
  return `well-${boreId}`;
}

/** The Borefield meta to carry on `Pad.legacyBorefield` / `Well.legacyBorefield` — a DEEP CLONE of
 *  everything except `bores` (re-attached on reconstruction) and `id` (becomes the FieldPad id).
 *  Cloned so the wrapper can't mutate the source Borefield's nested objects (address/geodetic/…). */
export function borefieldMetaOf(
  bf: Borefield,
): Omit<Borefield, "bores" | "id"> {
  const shallow: Record<string, unknown> = { ...bf };
  delete shallow.bores; // omit before cloning so we don't deep-copy the (possibly large) bores array
  delete shallow.id;
  // Drop explicit-`undefined`-valued keys: under exactOptionalPropertyTypes an optional means absent
  // OR a value — never `undefined`-present. structuredClone PRESERVES `{ operatorName: undefined }` as
  // a present key, and the `as` cast below would hide that violation, so a smuggled-in undefined would
  // survive into the carry and break a clean round-trip. Strip them so the carry is type-honest.
  for (const key of Object.keys(shallow)) {
    if (shallow[key] === undefined) delete shallow[key];
  }
  return structuredClone(shallow) as Omit<Borefield, "bores" | "id">;
}

/** Wrap one legacy Bore (+ its Borefield) into a canonical Well + its primary Wellbore.
 *  The primary wellbore's id == the Bore id (shim identity). Lossless: the full Bore is DEEP-CLONED
 *  into `primaryWellbore.legacyBore` and the Borefield meta into `well.legacyBorefield`. Promotes
 *  only the structural ids + `wellName` + `jobs` (see the MINIMAL PROMOTION note above). */
export function boreToWell(
  bore: Bore,
  borefield: Borefield,
): { well: Well; primaryWellbore: Wellbore } {
  const wellId = wellIdForBore(bore.id);

  const primaryWellbore: Wellbore = {
    id: bore.id, // SHIM IDENTITY: wellbore id == bore id
    wellId,
    name: "Original Hole",
    // jobs is the one operational field the new model reads (timeline + SIMOPS) — promoted as an
    // independent clone so editing it doesn't mutate the original bore's jobs.
    ...(bore.jobs !== undefined ? { jobs: structuredClone(bore.jobs) } : {}),
    legacyBore: structuredClone(bore), // lossless, mutation-isolated carry
  };

  const well: Well = {
    id: wellId,
    fieldPadId: borefield.id,
    wellName: bore.boreName,
    wellbores: [primaryWellbore],
    legacyBorefield: borefieldMetaOf(borefield),
    // COORDINATE PROMOTION (the deferred coord phase):
    // promote the bore's surface lat/lon to the spine-native Well fields the maps + cellars read
    // (mapPoints / cellarCreate). A verbatim string copy behind a validity guard — no math (Abs-2).
    // Unlike numAPI/afeNumber (ABSENT from the projection, so `...prior` alone preserves them), this
    // field IS reproduced here, so withPriorAudit RE-ASSERTS surfaceLatitude/Longitude from the PRIOR
    // record on re-upsert (like the audit fields) to preserve a spine-direct edit — see
    // borefieldFacade.withPriorAudit. wellToBoreView keeps reading latitude/longitude from `legacyBore`,
    // so the Bore round-trip is intact with no read-back override. Both set only when BOTH parse
    // (matches the pad anchor guard) — a half/garbage coord is dropped, not stored.
    ...(validCoordStr(bore.latitude) && validCoordStr(bore.longitude)
      ? {
          surfaceLatitude: String(bore.latitude),
          surfaceLongitude: String(bore.longitude),
        }
      : {}),
    // AUDIT PROMOTION (Bore→Well cutover Phase 6 Inc 1a): carry the Bore's audit stamps onto the
    // canonical Well so a read-time-migrated well isn't audit-blank on its own record — mirrors
    // borefieldToPad's Pad audit (migrateBorefields.ts). exactOptionalPropertyTypes-safe: spread only
    // present values (an absent stamp stays absent, never a smuggled `undefined`). The Bore also lives
    // verbatim in `legacyBore`, so wellToBoreView still reads these back from there (round-trip intact).
    ...(bore.createdAt ? { createdAt: bore.createdAt } : {}),
    ...(bore.createdBy ? { createdBy: bore.createdBy } : {}),
    ...(bore.modifiedAt ? { modifiedAt: bore.modifiedAt } : {}),
    ...(bore.modifiedBy ? { modifiedBy: bore.modifiedBy } : {}),
  };

  return { well, primaryWellbore };
}

/** Read-only Bore-shaped projection of a Well + one of its Wellbores, for legacy consumers during
 *  the transition. The PROMOTED canonical fields win (so a new-model edit surfaces) — distinguishing
 *  an edit-to-empty ("") from absent (undefined); everything else comes from the carried `legacyBore`.
 *  THROWS for a wellbore with no legacy carry (a wizard-created Well) — that path is not reachable in
 *  Phase 1 and must derive `boreType` etc. from canonical fields once a later phase needs it; failing
 *  fast beats inventing a wrong domain classification (Charter Absolute #4). */
export function wellToBoreView(well: Well, wellbore: Wellbore): Bore {
  const legacy = wellbore.legacyBore;
  if (!legacy) {
    throw new Error(
      "wellToBoreView: this Wellbore has no legacyBore carry — a Bore view of a wizard-created " +
        "Well is not supported until a later cutover phase promotes the canonical classification " +
        "(EPIC_BORE_TO_WELL_CUTOVER.md). Do not call wellToBoreView on a Well-native wellbore.",
    );
  }
  return {
    ...legacy,
    id: wellbore.id,
    // Promoted fields, read back from their canonical home (symmetric with boreToWell). `!== undefined`
    // (not truthiness) so a deliberate clear-to-"" surfaces instead of resurrecting the legacy value.
    boreName: well.wellName !== undefined ? well.wellName : legacy.boreName,
    ...(wellbore.jobs !== undefined ? { jobs: wellbore.jobs } : {}),
  };
}
